import type {
  AccessSpecifier,
  AttachmentFileData,
  AttachmentResultType,
} from "shared/types";
import type { ReplyToMessageData } from "../store";
import { stripHtml } from "./stripHtml";

// ─── Types ───────────────────────────────────────────────────────────

/**
 * Serializable subset of AttachmentFileData for persistence. The raw `file`
 * (File) and `controller` (AbortController) fields cannot be JSON-serialized,
 * and only fully uploaded files carry a usable server `responseData`.
 */
export interface PersistedAttachment {
  key: string;
  name: string;
  size: string;
  type: string;
  status: "Uploaded";
  progress: 100;
  responseData: AttachmentResultType;
}

export interface PersistedChatDraft {
  html: string;
  attachments: PersistedAttachment[];
  replyTo: ReplyToMessageData | null;
  sendMode: AccessSpecifier;
  updatedAt: number;
}

// ─── Key builder ─────────────────────────────────────────────────────

const CHAT_DRAFT_PREFIX = "chatDraft:";

const getDraftKey = (serviceId: string): string =>
  `${CHAT_DRAFT_PREFIX}${serviceId}`;

// ─── Attachment mapping ──────────────────────────────────────────────

/** Keep only fully uploaded files, stripped down to serializable fields. */
export const toPersistedAttachments = (
  files: AttachmentFileData[],
): PersistedAttachment[] =>
  files
    .filter((f) => f.status === "Uploaded")
    .map((f) => ({
      key: f.key,
      name: f.name,
      size: f.size,
      type: f.type,
      status: "Uploaded" as const,
      progress: 100 as const,
      responseData: f.responseData,
    }));

// ─── Emptiness check ─────────────────────────────────────────────────

/**
 * A draft is empty when there is no visible text, no inline image, no
 * attachment, and no active reply. Send mode alone never keeps a draft alive.
 */
export const isDraftEmpty = (
  html: string,
  attachments: PersistedAttachment[],
  replyTo: ReplyToMessageData | null,
): boolean => {
  const hasText = stripHtml(html).trim().length > 0;
  const hasInlineImage = html.includes("<img");
  return !hasText && !hasInlineImage && attachments.length === 0 && !replyTo;
};

// ─── Storage (try/catch guarded for private-mode / quota) ────────────

export const readChatDraft = (
  serviceId: string,
): PersistedChatDraft | null => {
  try {
    const raw = localStorage.getItem(getDraftKey(serviceId));
    if (!raw) return null;
    return JSON.parse(raw) as PersistedChatDraft;
  } catch {
    return null;
  }
};

export const writeChatDraft = (
  serviceId: string,
  draft: PersistedChatDraft,
): void => {
  try {
    localStorage.setItem(getDraftKey(serviceId), JSON.stringify(draft));
  } catch {
    // Ignore quota / private-mode write failures.
  }
};

export const removeChatDraft = (serviceId: string): void => {
  try {
    localStorage.removeItem(getDraftKey(serviceId));
  } catch {
    // Ignore private-mode failures.
  }
};

// ─── Text added from outside the editor ──────────────────────────────

/**
 * Appends a paragraph of plain text to a service's stored draft, creating the
 * draft if there is none. For words that arrive after the composer unmounted
 * (a dictation finalised on leave), so they're in the editor on return.
 */
export const appendTextToChatDraft = (
  serviceId: string,
  text: string,
  sendMode: AccessSpecifier,
): void => {
  // textContent escapes the text: dictated words can't inject markup.
  const paragraph = document.createElement("p");
  paragraph.textContent = text;
  const draft = readChatDraft(serviceId);
  writeChatDraft(serviceId, {
    html: `${draft?.html ?? ""}${paragraph.outerHTML}`,
    attachments: draft?.attachments ?? [],
    replyTo: draft?.replyTo ?? null,
    sendMode: draft?.sendMode ?? sendMode,
    updatedAt: Date.now(),
  });
};
